import { env } from '@/lib/env';
import { ApiError, parseApiError } from '@/lib/api-error';
import { RefreshResponseSchema, type RefreshResponse } from '@/lib/schemas/common.schema';
import { UserSchema, type User } from '@/lib/schemas/user.schema';

// Llamadas crudas (`fetch`) de la sesión de Effetá Apps: pedir el pase
// (`POST /auth/refresh`) y el perfil (`GET /auth/me`). Viven fuera de
// `http.ts` para que el session store y el interceptor de `http.ts` las usen
// sin dependencia circular (http -> session store -> http).
//
// Particularidades del refresh:
// - `credentials: 'include'` obligatorio: el back lee su cookie de sesión
//   (httpOnly, del host del API) para entregar el pase.
// - Sin header Authorization.
// - Sin reintento acá: qué hacer con cada error lo decide el session store
//   (ver `classifySessionFailure`).

// Tipos de error del contrato de auth (`error.type` del sobre, expuesto como
// `ApiError.errorCode`).
export const EFFETA_ROLE_UNKNOWN = 'EFFETA_ROLE_UNKNOWN';
export const IDENTITY_PROVIDER_UNAVAILABLE = 'IDENTITY_PROVIDER_UNAVAILABLE';

function buildUrl(path: string): string {
  const base = env.VITE_API_BASE_URL.replace(/\/+$/, '');
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${base}${suffix}`;
}

// El back envuelve en `{ data, error, meta }`. Desempacamos a mano: no podemos
// usar `unwrapEnvelope` de http.ts sin reintroducir el ciclo.
function unwrapData(raw: unknown): unknown {
  return raw !== null && typeof raw === 'object' && 'data' in raw && 'error' in raw
    ? (raw as { data: unknown }).data
    : raw;
}

async function send(path: string, init: RequestInit): Promise<unknown> {
  let response: Response;
  try {
    response = await fetch(buildUrl(path), init);
  } catch (cause) {
    throw new ApiError({
      statusCode: 0,
      message: cause instanceof Error ? cause.message : 'Network error',
    });
  }

  if (!response.ok) {
    let errorBody: unknown = null;
    try {
      errorBody = await response.clone().json();
    } catch {
      /* sin body */
    }
    throw parseApiError(response, errorBody);
  }

  return unwrapData((await response.json()) as unknown);
}

export async function refreshSession(): Promise<RefreshResponse> {
  const data = await send('/auth/refresh', {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  return RefreshResponseSchema.parse(data);
}

// Perfil de quien tiene el pase: rol, permisos, áreas y tour. Se pide al
// arrancar y después de cada renovación (un cambio de rol en Effetá llega con
// el pase nuevo).
export async function fetchCurrentUser(token: string): Promise<User> {
  const data = await send('/auth/me', {
    method: 'GET',
    headers: { Accept: 'application/json', Authorization: `Bearer ${token}` },
  });
  return UserSchema.parse(data);
}

// Qué significa un error del refresh (o del `/auth/me` que lo sigue):
// - `signed-out`: 401, no hay sesión del PIM. Olvidar el pase y mostrar la
//   entrada.
// - `no-access`: 403 `EFFETA_ROLE_UNKNOWN`, el rol de Effetá no es un rol del
//   PIM. La sesión se cerró; pantalla "sin acceso", nunca mandar a entrar sola.
// - `transient`: 503 `IDENTITY_PROVIDER_UNAVAILABLE`, 429, red caída o
//   cualquier otro. La sesión sigue abierta: reintentar sin cerrarla.
export type SessionFailure = 'signed-out' | 'no-access' | 'transient';

export function classifySessionFailure(error: unknown): SessionFailure {
  if (error instanceof ApiError) {
    if (error.statusCode === 401) return 'signed-out';
    if (error.statusCode === 403 && error.errorCode === EFFETA_ROLE_UNKNOWN) return 'no-access';
  }
  return 'transient';
}
